import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { taskService, type TaskDetail } from '@/services/task.service';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface EditUnifiedTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: TaskDetail;
  members?: { id: string; name: string }[];
  sprintGoals?: { id: string; title: string }[];
  columns?: { id: string; name: string }[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function EditUnifiedTaskDialog({
  open,
  onOpenChange,
  task,
  members = [],
  sprintGoals = [],
  columns = [],
}: EditUnifiedTaskDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // ── Form state ──
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || '');
  const [assigneeId, setAssigneeId] = useState(task.assignee_id || '');
  const [priority, setPriority] = useState(task.priority);
  const [phaseStatus, setPhaseStatus] = useState(task.phase_status);
  const [columnId, setColumnId] = useState(task.column_id || '');
  const [startDate, setStartDate] = useState<Date | undefined>(
    task.start_date ? new Date(task.start_date) : undefined,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    task.end_date ? new Date(task.end_date) : undefined,
  );
  const [estimatedHours, setEstimatedHours] = useState(
    task.estimated_hours != null ? String(task.estimated_hours) : '',
  );
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>(
    task.sprint_goals?.map((g) => g.id) ?? [],
  );

  // Reset form when task changes
  useEffect(() => {
    setTitle(task.title);
    setDescription(task.description || '');
    setAssigneeId(task.assignee_id || '');
    setPriority(task.priority);
    setPhaseStatus(task.phase_status);
    setColumnId(task.column_id || '');
    setStartDate(task.start_date ? new Date(task.start_date) : undefined);
    setEndDate(task.end_date ? new Date(task.end_date) : undefined);
    setEstimatedHours(task.estimated_hours != null ? String(task.estimated_hours) : '');
    setSelectedGoalIds(task.sprint_goals?.map((g) => g.id) ?? []);
  }, [task]);

  // ── Mutation ──
  const updateMut = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {};

      if (title !== task.title) payload.title = title;
      if (description !== (task.description || '')) payload.description = description || null;
      if (assigneeId && assigneeId !== (task.assignee_id || '')) payload.assignee_id = assigneeId;
      if (priority !== task.priority) payload.priority = priority;
      if (phaseStatus !== task.phase_status) payload.phase_status = phaseStatus;
      if (columnId && columnId !== (task.column_id || '')) payload.column_id = columnId;

      if (startDate) {
        const formatted = format(startDate, 'yyyy-MM-dd');
        if (formatted !== task.start_date?.slice(0, 10)) payload.start_date = formatted;
      }
      if (endDate) {
        const formatted = format(endDate, 'yyyy-MM-dd');
        if (formatted !== task.end_date?.slice(0, 10)) payload.end_date = formatted;
      }

      const estHrs = estimatedHours ? parseFloat(estimatedHours) : null;
      if (estHrs !== task.estimated_hours) payload.estimated_hours = estHrs;

      // Sprint goals
      const prevGoalIds = (task.sprint_goals ?? []).map((g) => g.id).sort().join(',');
      const newGoalIds = [...selectedGoalIds].sort().join(',');
      if (newGoalIds !== prevGoalIds) {
        payload.sprint_goal_ids = selectedGoalIds;
      }

      return taskService.update(task.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', task.id] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (task.sprint_id) {
        queryClient.invalidateQueries({ queryKey: ['sprint-tasks', task.sprint_id] });
        queryClient.invalidateQueries({ queryKey: ['sprint', task.sprint_id] });
      }
      if (task.goal_id) {
        queryClient.invalidateQueries({ queryKey: ['goal-tasks', task.goal_id] });
      }
      toast({ title: 'Task updated' });
      onOpenChange(false);
    },
    onError: (e: any) =>
      toast({
        title: 'Error',
        description: e.response?.data?.error?.message || 'Failed to update task',
        variant: 'destructive',
      }),
  });

  function toggleGoal(goalId: string) {
    setSelectedGoalIds((prev) =>
      prev.includes(goalId) ? prev.filter((id) => id !== goalId) : [...prev, goalId],
    );
  }

  const isSprint = task.task_type === 'sprint';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Task</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateMut.mutate();
          }}
          className="space-y-4"
        >
          {/* Title */}
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              placeholder="Task title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Description</Label>
            <textarea
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y"
              placeholder="Task description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Assignee */}
          <div className="space-y-2">
            <Label>Assignee *</Label>
            <Select value={assigneeId} onValueChange={setAssigneeId}>
              <SelectTrigger>
                <SelectValue placeholder="Select assignee" />
              </SelectTrigger>
              <SelectContent>
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Priority + Phase Status side by side */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Phase Status</Label>
              <Select value={phaseStatus} onValueChange={setPhaseStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Column (sprint tasks only) */}
          {isSprint && columns.length > 0 && (
            <div className="space-y-2">
              <Label>Column</Label>
              <Select value={columnId || 'none'} onValueChange={(v) => setColumnId(v === 'none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select column" />
                </SelectTrigger>
                <SelectContent>
                  {columns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Start Date + End Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full pl-3 text-left font-normal',
                      !startDate && 'text-muted-foreground',
                    )}
                  >
                    {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>End Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full pl-3 text-left font-normal',
                      !endDate && 'text-muted-foreground',
                    )}
                  >
                    {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Estimated Hours */}
          <div className="space-y-2">
            <Label>Estimated Hours</Label>
            <Input
              type="number"
              step="0.5"
              min="0"
              placeholder="e.g. 8"
              value={estimatedHours}
              onChange={(e) => setEstimatedHours(e.target.value)}
            />
          </div>

          {/* Tags (display only) */}
          {task.tags && task.tags.length > 0 && (
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-1.5">
                {task.tags.map((tag) => (
                  <Badge key={tag.id} variant="secondary" className="text-xs">
                    {tag.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Sprint Goals (sprint tasks only) */}
          {isSprint && sprintGoals.length > 0 && (
            <div className="space-y-2">
              <Label>Sprint Goals</Label>
              <div className="flex flex-wrap gap-1.5 border rounded-md p-2">
                {sprintGoals.map((sg) => (
                  <Badge
                    key={sg.id}
                    variant={selectedGoalIds.includes(sg.id) ? 'default' : 'outline'}
                    className={cn(
                      'cursor-pointer select-none transition-colors',
                      selectedGoalIds.includes(sg.id)
                        ? 'bg-brand-primary hover:bg-brand-primary-dark text-white'
                        : 'hover:bg-muted',
                    )}
                    onClick={() => toggleGoal(sg.id)}
                  >
                    {sg.title}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-brand-primary hover:bg-brand-primary-dark"
              disabled={!title.trim() || !assigneeId || !startDate || !endDate || updateMut.isPending}
            >
              {updateMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
